import { useMutation, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import FastAPI from "../../api/FastAPI";
import SCHEDULE_LIST_MUTATION from "../../constants/mutationKey";
import {
  getCurrentTimelineQuery,
  invalidateTimelineRelatedQueries,
} from "../../utils/fast";
import useFASTScheduleWebSocket from "../useFASTScheduleWebSocket";
import useTimebounds from "../useTimebounds";

import type { UseMutationResult } from "@tanstack/react-query";

import type { DeleteScheduleListMediaResponse } from "../../api/FastAPI.types";
import type { FASTChannelRoutesParams } from "../../components/routes/FASTChannelRoutes";

interface Params {
  mediaUuidToDelete: string;
  mediaToAdd: {
    mediaUuid: string;
    startDate: number;
  };
  scheduleId: string;
}

function useReplaceScheduleListMediaMutation(): UseMutationResult<
  DeleteScheduleListMediaResponse,
  unknown,
  Params
> {
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const { t } = useTranslation("editor-fast-channel");
  const queryClient = useQueryClient();

  const { currentDay } = useTimebounds();
  const { confirmUpdatedSchedule } = useFASTScheduleWebSocket();

  const deleteScheduleListMediaMutation = useMutation({
    mutationKey: [SCHEDULE_LIST_MUTATION, channelId],
    mutationFn: async ({
      mediaUuidToDelete,
      scheduleId,
    }: Params): Promise<DeleteScheduleListMediaResponse> =>
      FastAPI.deleteScheduleListMedia({
        channelId,
        scheduleId,
        uuid: mediaUuidToDelete,
      }),
    onSuccess: async (_, params): Promise<void> => {
      const { mediaToAdd, scheduleId } = params;

      const media = {
        object_uuid: mediaToAdd.mediaUuid,
        start_time: moment(mediaToAdd.startDate).toISOString(),
      };

      try {
        await FastAPI.addScheduleListMedia({
          channelId,
          scheduleId,
          media,
        });
      } catch (error) {
        toast.error(t("programReplaceError"));
        console.error("addScheduleListMedia", error, params);
      }

      invalidateTimelineRelatedQueries({
        currentDay,
        channelId,
        queryClient,
        scheduleId,
      });

      confirmUpdatedSchedule(currentDay);

      toast.success(t("programReplaceSuccess"));
    },
    onError: (error, params): void => {
      toast.error(t("programReplaceError"));
      console.error("deleteScheduleListMedia", error, params);
    },
    onSettled: (): void => {
      const { previousQuery, currentQuery, nextQuery } =
        getCurrentTimelineQuery({
          currentDay,
          channelId,
        });
      queryClient.invalidateQueries(previousQuery);
      queryClient.invalidateQueries(currentQuery);
      queryClient.invalidateQueries(nextQuery);
    },
  });

  return deleteScheduleListMediaMutation;
}

export default useReplaceScheduleListMediaMutation;
